// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { handleActions } from 'redux-actions';

import {
    ACTION_STATUS_FAILED,
    ACTION_STATUS_RESET,
    ACTION_STATUS_STARTED,
    ACTION_STATUS_SUCCESS,
    ACTION_STATUS_ACCEPTED,
    RESET_ACTIONS_DATA,
} from './actions';

import { LoadingState } from './actionStatusTypes';

import { logTraceEvent } from './../../lib/ApplicationInsights';
import { removeAuthHeaders } from '../../helpers/sanitization';

interface IInitialState {
    [key: string]: {
        status: LoadingState;
        error?: {};
    };
}

const INITIAL_STATE: IInitialState = {};

export default handleActions(
    {
        [ACTION_STATUS_STARTED]: (state, action: any) => {
            return {
                ...state,
                [action.payload.actionName]: {
                    status: LoadingState.started,
                },
            };
        },
        [ACTION_STATUS_SUCCESS]: (state, action: any) => {
            return {
                ...state,
                [action.payload.actionName]: {
                    status: LoadingState.success,
                    data: action.payload.data,
                },
            };
        },
        [ACTION_STATUS_ACCEPTED]: (state, action: any) => {
            return {
                ...state,
                [action.payload.actionName]: {
                    status: LoadingState.accepted,
                    data: action.payload.data,
                },
            };
        },
        [ACTION_STATUS_FAILED]: (state, action: any) => {
            const {
                message,
                response = { data: {}, status: '' },
                stack,
            } = action.payload.error;

            if (action?.payload?.error) {
                action.payload.error = removeAuthHeaders(action.payload.error);
            }
                
            const traceName = `ACTION_STATUS_FAILED: ${action.payload.actionName}`;
            const traceData = {
                actionPayload: JSON.stringify({ ...action.payload, error: undefined }).substring(0, 2048),
                errorDetails: JSON.stringify({ ...action.payload.error, message: undefined, response: undefined, stack: undefined }).substring(0, 2048),
                errorMessage: message,
                errorResponse: JSON.stringify(response).substring(0, 2048),
                errorStack: JSON.stringify(stack || {}).substring(0, 512),
                name: traceName,
                ...{ ...action, payload: undefined },
            };
            logTraceEvent(traceName, traceData);

            return {
                ...state,
                [action.payload.actionName]: {
                    status: LoadingState.rejected,
                    payload: action.payload.data,
                    error: {
                        message,
                        stack,
                        data: response.data,
                        status: response.status,
                    },
                },
            };
        },
        [ACTION_STATUS_RESET]: (state, action: any) => {
            return {
                ...state,
                [action.payload.actionName]: {
                    status: LoadingState.notStarted,
                },
            };
        },
        [RESET_ACTIONS_DATA]: (state, action: any) => {
            if (action.payload) {
                const { [action.payload]: value, ...newState } = state;
                return {
                    ...newState,
                };
            }

            return { ...INITIAL_STATE };
        },
    },
    INITIAL_STATE
);
